import React, { useState, useEffect, useCallback } from 'react';
import { useApi, useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { Hammer, Play } from 'lucide-react';

const C = { navy: '#0D1B2A', blue: '#1A4C7C', green: '#1D9E75', amber: '#E8A020', slate: '#5C6B7A' };

export function PlatformModeSwitch({ compact = false }) {
  const api = useApi();
  const { user } = useAuth();
  const canFlip = ['platform_owner', 'mga_admin'].includes(user?.role);
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => api.get('/platform/mode').then(r => setStatus(r.data)).catch(() => {}), [api]);
  useEffect(() => { load(); }, [load]);

  const flip = async (mode) => {
    if (!canFlip || busy || !status || status.mode === mode) return;
    setBusy(true);
    try {
      const { data } = await api.put('/platform/mode', { mode });
      setStatus(data);
      toast.success(mode === 'operate'
        ? `Operate mode — ${data.started?.length || 0} worker(s) started`
        : `Build mode — ${data.stopped?.length || 0} worker(s) quieted`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not switch mode');
    } finally { setBusy(false); }
  };

  if (!status) return null;
  const build = status.mode === 'build';
  const color = build ? C.amber : C.green;
  return (
    <div className={`flex items-center gap-3 ${compact ? '' : 'p-3 rounded-sm bg-white'}`}
      style={compact ? {} : { border: `1px solid ${color}40` }} data-testid="platform-mode-switch">
      {build ? <Hammer className="w-4 h-4 shrink-0" style={{ color }} /> : <Play className="w-4 h-4 shrink-0" style={{ color }} />}
      <div className="min-w-0">
        <p className="text-xs font-semibold" style={{ color: C.navy }}>
          Platform in <span style={{ color }} data-testid="platform-mode-value">{status.mode}</span> mode
        </p>
        {!compact && (
          <p className="text-[10px]" style={{ color: C.slate }}>
            {build ? `Self-operating workers quiet: ${status.quieted.join(', ')}` : 'Autonomy, simulation, enrichment and the eval loop run under their rulings'}
          </p>
        )}
      </div>
      {canFlip && (
        <div className="ml-auto flex rounded-sm overflow-hidden shrink-0" style={{ border: `1px solid rgba(136,150,168,0.3)` }}>
          {['build', 'operate'].map(m => (
            <button key={m} type="button" onClick={() => flip(m)} disabled={busy} data-testid={`platform-mode-${m}-btn`}
              className="px-3 py-1 text-[11px] font-semibold capitalize transition-colors"
              style={status.mode === m ? { background: C.blue, color: 'white' } : { background: 'white', color: C.slate }}>
              {m}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
